/**
 * Per-peer standing-latency edge detector over `classifyStandingLatency`.
 *
 * Says when a standing buffer BEGINS (the classification has held for `holdMs`) and when it
 * ENDS, with how long it stood and the peak excess, so the health record carries two events
 * per episode instead of one per tick. Mirrors the shape of `DecoderBackupTracker`.
 *
 * While standing it also judges the H1/H2 depth hypothesis across frame-rate changes:
 *  - H1 `time-constant`: depth in ms holds while fps moves -> a time-based target (jitter
 *    buffer / timing module). Slowing arrival below playout drains it.
 *  - H2 `frame-constant`: depth in frames holds while fps moves -> frames in flight inside
 *    the decoder. Fewer fps makes the wait LONGER.
 * Rule (frozen in PHASE_3.md): keep the last N=6 (deliveredFps, standingBacklogFrames,
 * jitterBufferDelayMs) triples while standing; if fps changed by >= 25% across the window
 * and depth-ms varied < 20% -> 'time-constant'; if depth-frames varied < 20% ->
 * 'frame-constant'; else 'undetermined'. When a guest sends no jitterBufferDelayMs (pre
 * Phase 2), depth-ms is derived as frames x one frame interval at that tick's fps.
 *
 * The verdict's `kind` is the raw classification of the latest tick; the hold gates the
 * TRANSITION (and so the events), not the classification, so a consumer that must stay
 * conservative (reclaim) sees a standing buffer the moment it is measured.
 *
 * Browser-free and Node/Jest-safe.
 */

import { classifyStandingLatency } from './standingLatencyClassifier';
import {
  DEFAULT_STANDING_LATENCY_THRESHOLDS,
  type StandingDepthHypothesis,
  type StandingLatencyInputs,
  type StandingLatencyThresholds,
  type StandingLatencyVerdict,
} from './standingLatencyContracts';

export type StandingLatencyTransition =
  /** Standing past the hold for the first time in this episode. */
  | 'began'
  /** Reported begun and still standing. */
  | 'continuing'
  /** Was reported begun; now clear. */
  | 'ended'
  /** Not standing, and nothing was begun. */
  | 'clear'
  /** Standing, but the hold has not elapsed yet. */
  | 'holding';

export type StandingLatencyObservation = {
  verdict: StandingLatencyVerdict;
  transition: StandingLatencyTransition;
  /** Present on 'ended': how long it stood and the peak excess seen. */
  ended?: { durationMs: number; peakExcessMs: number };
};

/** One standing tick's depth reading, kept for the hypothesis window. */
export type DepthTriple = {
  deliveredFps: number;
  standingBacklogFrames?: number;
  depthMs?: number;
};

export const DEPTH_HYPOTHESIS_WINDOW = 6;
export const DEPTH_HYPOTHESIS_MIN_FPS_CHANGE = 0.25;
export const DEPTH_HYPOTHESIS_MAX_DEPTH_VARIATION = 0.2;

type PeerStanding = {
  sinceMs: number | null;
  begun: boolean;
  /** While begun: when the classification first stopped being standing-buffer, or null. */
  releasingSinceMs: number | null;
  peakExcessMs: number;
  triples: DepthTriple[];
  verdict: StandingLatencyVerdict;
};

/** (max - min) / max over the values, or null with fewer than two readings. */
function relativeSpread(values: number[]): number | null {
  if (values.length < 2) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  if (max <= 0) return null;
  return (max - min) / max;
}

/** Pure: the H1/H2 discriminator over a window of standing ticks. */
export function judgeDepthHypothesis(triples: ReadonlyArray<DepthTriple>): StandingDepthHypothesis {
  const fpsSpread = relativeSpread(triples.map((t) => t.deliveredFps));
  if (fpsSpread === null || fpsSpread < DEPTH_HYPOTHESIS_MIN_FPS_CHANGE) return 'undetermined';
  const msSpread = relativeSpread(triples.map((t) => t.depthMs).filter(isFiniteNumber));
  if (msSpread !== null && msSpread < DEPTH_HYPOTHESIS_MAX_DEPTH_VARIATION) return 'time-constant';
  const frameSpread = relativeSpread(triples.map((t) => t.standingBacklogFrames).filter(isFiniteNumber));
  if (frameSpread !== null && frameSpread < DEPTH_HYPOTHESIS_MAX_DEPTH_VARIATION) return 'frame-constant';
  return 'undetermined';
}

function tripleFor(inputs: StandingLatencyInputs): DepthTriple | null {
  if (!isFiniteNumber(inputs.deliveredFps) || inputs.deliveredFps <= 0) return null;
  const derivedMs = isFiniteNumber(inputs.standingBacklogFrames)
    ? (inputs.standingBacklogFrames * 1000) / inputs.deliveredFps
    : undefined;
  const depthMs = isFiniteNumber(inputs.jitterBufferDelayMs) ? inputs.jitterBufferDelayMs : derivedMs;
  return {
    deliveredFps: inputs.deliveredFps,
    ...(isFiniteNumber(inputs.standingBacklogFrames) ? { standingBacklogFrames: inputs.standingBacklogFrames } : {}),
    ...(depthMs !== undefined ? { depthMs } : {}),
  };
}

export class StandingLatencyDetector {
  private readonly thresholds: StandingLatencyThresholds;
  private readonly nowMs: () => number;
  private readonly peers = new Map<string, PeerStanding>();

  constructor(opts: { thresholds?: StandingLatencyThresholds; nowMs?: () => number } = {}) {
    this.thresholds = opts.thresholds ?? DEFAULT_STANDING_LATENCY_THRESHOLDS;
    this.nowMs = opts.nowMs ?? (() => Date.now());
  }

  /** Observe one tick for a peer. */
  public observe(peerId: string, inputs: StandingLatencyInputs): StandingLatencyObservation {
    const classified = classifyStandingLatency(inputs, this.thresholds);
    const now = this.nowMs();
    const state = this.peers.get(peerId) ?? freshState();
    if (inputs.measurementFresh !== true) {
      const verdict: StandingLatencyVerdict = { ...classified, evidenceState: 'unavailable' };
      const ended = state.begun && state.sinceMs !== null
        ? { durationMs: now - state.sinceMs, peakExcessMs: state.peakExcessMs } : undefined;
      this.peers.set(peerId, { ...freshState(), verdict });
      return { verdict, transition: ended ? 'ended' : 'clear', ...(ended ? { ended } : {}) };
    }


    if (classified.kind !== 'standing-buffer') {
      // A begun episode must be absent for releaseHoldMs before it ends: a single tick of trend
      // or queue jitter is not a drained buffer. While releasing, the verdict stays
      // standing-buffer (the frames have not gone anywhere) and the transition is continuing.
      if (state.begun && state.sinceMs !== null) {
        const releasingSince = state.releasingSinceMs ?? now;
        if (now - releasingSince < this.thresholds.releaseHoldMs) {
          const verdict: StandingLatencyVerdict = {
            kind: 'standing-buffer',
            evidenceState: 'tentative',
            excessMs: classified.excessMs,
            depthHypothesis: judgeDepthHypothesis(state.triples),
          };
          this.peers.set(peerId, { ...state, releasingSinceMs: releasingSince, verdict });
          return { verdict, transition: 'continuing' };
        }
        const ended = { durationMs: releasingSince - state.sinceMs, peakExcessMs: state.peakExcessMs };
        const verdict = { ...classified, depthHypothesis: 'undetermined' as const };
        this.peers.set(peerId, { ...freshState(), verdict });
        return { verdict, transition: 'ended', ended };
      }
      const verdict = { ...classified, depthHypothesis: 'undetermined' as const };
      this.peers.set(peerId, { ...freshState(), verdict });
      return { verdict, transition: 'clear' };
    }

    state.releasingSinceMs = null;
    if (state.sinceMs === null) state.sinceMs = now;
    state.peakExcessMs = Math.max(state.peakExcessMs, classified.excessMs ?? 0);
    const triple = tripleFor(inputs);
    if (triple) state.triples = [...state.triples, triple].slice(-DEPTH_HYPOTHESIS_WINDOW);
    const verdict: StandingLatencyVerdict = { ...classified, depthHypothesis: judgeDepthHypothesis(state.triples) };
    state.verdict = verdict;

    let transition: StandingLatencyTransition;
    if (state.begun) {
      transition = 'continuing';
    } else if (now - state.sinceMs >= this.thresholds.holdMs) {
      state.begun = true;
      transition = 'began';
    } else {
      transition = 'holding';
    }
    verdict.evidenceState = state.begun ? 'confirmed' : 'tentative';
    this.peers.set(peerId, state);
    return { verdict, transition };
  }

  /** The latest verdict for a peer, or undefined when never observed. */
  public current(peerId: string): StandingLatencyVerdict | undefined {
    return this.peers.get(peerId)?.verdict;
  }

  public clearPeer(peerId: string): void {
    this.peers.delete(peerId);
  }

  public clearAll(): void {
    this.peers.clear();
  }
}

function freshState(): PeerStanding {
  return {
    sinceMs: null,
    begun: false,
    releasingSinceMs: null,
    peakExcessMs: 0,
    triples: [],
    verdict: { evidenceState: 'unavailable', kind: 'unknown', excessMs: null, depthHypothesis: 'undetermined' },
  };
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
